import { useMemo } from 'react';
import {
  AloftPrice,
  FinancingType,
  schAloftPrice,
  schFinancingType,
  syntaxFNMA,
} from '@aloft/toolkit-shared';
import { toOpts } from '@aloft/toolkit-sui';

import { PropertyGridSection } from '../../../components/PropertyGrid/columnSections/PropertyGridSection.tsx';
import {
  getNumberPropertyGridField,
  suffixField,
} from '../../../components/PropertyGrid/fields/utils.tsx';
import {
  PropertyGridField,
  PropertyGridSectionProps,
} from '../../../components/PropertyGrid/types.ts';

export const FeatureGridTransactionSection = (props: PropertyGridSectionProps) => {
  const { columnIndex, getValues } = props;

  const financingTypeField: PropertyGridField<string, FinancingType> = useMemo(
    () => ({
      label: 'Financing Type',
      fieldName: 'editableData.transaction.concessions',
      options: toOpts(schFinancingType.options),
      autofillSubjectValue: false,
      toDisplayValue: (concessions?: string) => {
        if (!concessions) return undefined;
        return syntaxFNMA.financialConcessionsSyntax.decode(concessions).financingType;
      },
      toValue: (value?: string) => {
        if (!value) return undefined;

        const propertyData = getValues(`columns.${columnIndex}.propertyData`);
        const { concessions } = propertyData?.editableData.transaction ?? {};

        const financialConcessionAmount = concessions
          ? syntaxFNMA.financialConcessionsSyntax.decode(concessions).financialConcessionAmount
          : undefined;

        return syntaxFNMA.financialConcessionsSyntax.encode({
          financingType: schFinancingType.parse(value),
          financialConcessionAmount,
        });
      },
      includeEmptyOption: true,
    }),
    [columnIndex, getValues],
  );

  const saleConcessionsField: PropertyGridField<string> = useMemo(
    () => ({
      label: 'Sale Concessions',
      fieldName: 'editableData.transaction.concessions',
      toDisplayValue: (concessions?: string) => {
        if (!concessions) return undefined;

        return syntaxFNMA.financialConcessionsSyntax
          .decode(concessions)
          .financialConcessionAmount?.toString(true);
      },
      toEditValue: (concessions?: string) => {
        if (!concessions) return undefined;

        const { financialConcessionAmount } =
          syntaxFNMA.financialConcessionsSyntax.decode(concessions);

        if (!financialConcessionAmount) return undefined;

        return financialConcessionAmount.toDollarLike().toString();
      },
      toValue: (value?: string) => {
        if (!value) return undefined;

        const propertyData = getValues(`columns.${columnIndex}.propertyData`);
        const { concessions } = propertyData?.editableData.transaction ?? {};

        const financingType = concessions
          ? syntaxFNMA.financialConcessionsSyntax.decode(concessions).financingType
          : undefined;

        const price = schAloftPrice.safeParse(value);
        if (price.success) {
          return syntaxFNMA.financialConcessionsSyntax.encode({
            financingType,
            financialConcessionAmount: price.data,
          });
        }

        // Handle partial input gracefully (e.g., just "$" or empty string)
        const trimmedValue = value.trim();
        if (trimmedValue === '' || trimmedValue === '$') {
          return syntaxFNMA.financialConcessionsSyntax.encode({
            financingType,
            financialConcessionAmount: undefined,
          });
        }

        try {
          return syntaxFNMA.financialConcessionsSyntax.encode({
            financingType,
            financialConcessionAmount: AloftPrice.fromDollarLike(value),
          });
        } catch {
          // If parsing fails, return undefined to allow partial input
          return syntaxFNMA.financialConcessionsSyntax.encode({
            financingType,
            financialConcessionAmount: undefined,
          });
        }
      },
    }),
    [columnIndex, getValues],
  );

  const fields = useMemo(
    () => [
      financingTypeField,
      saleConcessionsField,
      {
        fieldName: 'editableData.transaction.contractDate' as any,
        label: 'Contract Date',
        toDisplayValue: (isoMonth?: string) => {
          if (!isoMonth) return undefined;
          try {
            const date = syntaxFNMA.isoMonthToAloftDate(isoMonth as syntaxFNMA.IsoMonth);
            return date.format('yyyy-MM');
          } catch {
            return isoMonth;
          }
        },
      },
      {
        fieldName: 'editableData.transaction.saleDate' as any,
        label: 'Sale Date',
        toDisplayValue: (isoMonth?: string) => {
          if (!isoMonth) return undefined;
          try {
            const date = syntaxFNMA.isoMonthToAloftDate(isoMonth as syntaxFNMA.IsoMonth);
            return date.format('yyyy-MM');
          } catch {
            return isoMonth;
          }
        },
      },
      suffixField(
        getNumberPropertyGridField('editableData.transaction.daysOnMarket', 'CDOM'),
        'day',
        'days',
      ),
    ],
    [financingTypeField, saleConcessionsField],
  );

  return (
    <PropertyGridSection
      {...props}
      title="Transaction"
      fields={fields}
    />
  );
};
